import {
  getInAppAutoCookie,
  getWebAuthCookie,
} from "src/utils/authCookieParser";
import { appConfig } from "src/config";
import { captureException } from "src/utils/error";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import {
  isCompoundedMedTier,
  isCompoundedMicrodoseMedTier,
} from "src/utils/plans";
import { isContinuityOfCareEnabled } from "src/utils/userSegment/features";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import {
  setContinuityOfCareChecked,
  setContinuityOfCareEligible,
} from "src/utils/redux/slices/currentSubscriptionData";
import type { ActionHandlerParams } from "src/pageDefinitions/actions";
import {
  getNoomSessionStorage,
  setNoomSessionStorage,
} from "src/utils/noomSessionStorage";
import { SessionStorageKey } from "src/utils/constants";
import { getAuthorizationHeader } from "src/api/utils";
import { getActiveUser } from "@noom/hive-auth";
import type {
  SubscriptionProto,
  SubscriptionProto_Status,
} from "@noom/noom-contracts/noom_contracts/billing/subscription/v2/subscription";
import type { SearchSubscriptionsResponseProto } from "@noom/noom-contracts/noom_contracts/billing/subscription/v2/subscription_service";
import { trackTask } from "src/utils/monitoring/events/tasks";

const ACTIVE_STATUSES: SubscriptionProto_Status[] = ["ACTIVE", "PAST_DUE"];

/**
 * Searches for user subscriptions via the billing v2Proto API.
 * Throws on authentication or network failures.
 */
async function searchSubscriptions(params?: {
  maxResults?: number;
  statusFilter?: SubscriptionProto_Status[];
}): Promise<SubscriptionProto[]> {
  const task = trackTask("SearchSubscriptions", params);
  task.start();

  try {
    const accessCode = getActiveUser()?.accessCode;
    if (!accessCode) {
      const err = new Error("User not authenticated");
      throw err;
    }
    const queryParams = new URLSearchParams({ accessCode });
    if (params?.maxResults != null) {
      queryParams.set("maxResults", String(params.maxResults));
    }
    if (params?.statusFilter) {
      for (const status of params.statusFilter) {
        queryParams.append("statusFilter", status);
      }
    }
    const response = await fetch(
      `${appConfig.COACH_SERVER_URL}/billing/v2Proto/subscriptions:search?${queryParams}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthorizationHeader(),
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to search subscriptions: ${response.status}`);
    }

    const data: SearchSubscriptionsResponseProto = await response.json();
    task.success();
    return data.result ?? [];
  } catch (error) {
    task.failed(error);
    throw error;
  }
}

/**
 * Returns the user's active program subscription, or undefined if none exists.
 * Assumes at most one active PROGRAM subscription per user, enforced at creation
 * time by {@link https://github.com/noom/backend/blob/8c53038932691da4d575c87dac86d152eb3cd95d/com.noom.spring.base/src/main/java/com/noom/spring/billing/service/ConflictingSubscriptionDetectionService.kt#L97-L103 ConflictingSubscriptionDetectionService}.
 * Throws on auth or network failures.
 */
export async function getActiveProgramSubscription(): Promise<
  SubscriptionProto | undefined
> {
  const subscriptions = await searchSubscriptions({
    statusFilter: ACTIVE_STATUSES,
  });
  return subscriptions.find((s) => s.productType === "PROGRAM");
}

/**
 * Checks user subscription history for compounded semaglutide subscriptions.
 * Sets continuityOfCareEligible flag in Redux if compounded history is found.
 * This flag is used to make users eligible for Personalized Dosing based on continuity of care.
 *
 * Also validates that the user hasn't entered a different email during the flow,
 * which would indicate a different person and prevent continuity of care from applying.
 */
// TODO: [MGEXP-2837]: This will use offers service instead of direct API calls.
const continuityCheckPromises = new WeakMap<
  ActionHandlerParams["store"],
  Promise<void>
>();

export async function checkAndSetContinuityOfCareStatus({
  store,
}: Pick<ActionHandlerParams, "store">): Promise<void> {
  if (!isContinuityOfCareEnabled()) {
    return;
  }

  const { email: surveyEmail } = getSurveyAnswers(store.getState());

  if (surveyEmail) {
    const inAppAuthCookie = getInAppAutoCookie();
    const webAuthCookie = getWebAuthCookie();
    const cookieEmail = inAppAuthCookie?.email || webAuthCookie?.email;

    if (!cookieEmail) {
      await store.dispatch(setContinuityOfCareEligible(false));
      return;
    }

    if (surveyEmail.toLowerCase() !== cookieEmail.toLowerCase()) {
      trackBuyflowEvent("ContinuityOfCareEmailMismatched", {});
      await store.dispatch(setContinuityOfCareEligible(false));
      return;
    }
  }

  const { currentSubscriptionData } = store.getState();

  // Check if we're in a new browser session
  const hasCheckedInSession = getNoomSessionStorage<boolean>(
    SessionStorageKey.continuityOfCareCheck
  );

  if (currentSubscriptionData.continuityOfCareChecked && hasCheckedInSession) {
    return;
  }

  const existingPromise = continuityCheckPromises.get(store);
  if (existingPromise) {
    await existingPromise;
    return;
  }

  const continuityCheckPromise = (async () => {
    // If it's a new session, reset the checked flag to ensure fresh check
    const isNewSession = !hasCheckedInSession;
    if (isNewSession) {
      setNoomSessionStorage(SessionStorageKey.continuityOfCareCheck, true);
      await store.dispatch(setContinuityOfCareChecked(false));
    }

    // TODO: [MGEXP-2837]: Migrate continuity of care checks to offers service
    // instead of using searchSubscriptions directly.
    let subscriptions: SubscriptionProto[];
    try {
      subscriptions = await searchSubscriptions({ maxResults: 50 });
    } catch (error) {
      captureException(error, "continuity-of-care-search-failed");
      await store.dispatch(setContinuityOfCareChecked(true));
      // Reset eligibility only in new session on first API failure to avoid using stale data from localStorage
      if (isNewSession) {
        await store.dispatch(setContinuityOfCareEligible(false));
      }
      return;
    }

    const hasCompoundedHistory = subscriptions.some(
      (subscription) =>
        isCompoundedMedTier(subscription.planId) ||
        isCompoundedMicrodoseMedTier(subscription.planId)
    );

    if (hasCompoundedHistory) {
      await store.dispatch(setContinuityOfCareEligible(true));
    } else {
      await store.dispatch(setContinuityOfCareEligible(false));
    }

    await store.dispatch(setContinuityOfCareChecked(true));
  })();

  continuityCheckPromises.set(store, continuityCheckPromise);

  try {
    await continuityCheckPromise;
  } finally {
    continuityCheckPromises.delete(store);
  }
}
